import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Grid,
} from '@mui/material';

// MESSAGES
import PaymentMethodFormattedMessages from '../../messages/billing/PaymentMethodFormattedMessages';

// NETWORKING
import usePaymentMethodRequest from '../../requests/PaymentMethodRequest';

// ROUTES
import { BillingRoutes } from '../../routes/BillingRoutes';

// COMPONENTS
import stripeLogo from '../../images/Powered by Stripe - blurple.svg';
import Loader from '../../components/common/Loader';
import Layout from '../../components/layouts/Layout';
import ErrorList from '../../components/common/ErrorList';

const cardPatterns = {
  Visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
  Mastercard:
    /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/,
  Discover:
    /^65[4-9][0-9]{13}|64[4-9][0-9]{13}|6011[0-9]{12}|(622(?:12[6-9]|1[3-9][0-9]|[2-8][0-9][0-9]|9[01][0-9]|92[0-5])[0-9]{10})$/,
  'American Express': /^3[47][0-9]{13}$/,
};

const PaymentMethod = ({ customer, accessLevel, accessType, errorHandler }) => {
  const { getCardDetails, saveCardDetails } = usePaymentMethodRequest();
  const [cardDetails, setCardDetails] = useState({
    name: '',
    last_four: '',
    expiry: '',
  });
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [cardUpdate, setCardUpdate] = useState(false);
  const [errors, setErrors] = useState([]);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchCardDetails = async () => {
      if (customer) {
        const { cc_name, cc_expiry, cc_last_four } = await getCardDetails(
          errorHandler
        );
        setCardDetails({
          name: cc_name || 'N/A',
          last_four: cc_last_four || 'N/A',
          expiry: cc_expiry || 'N/A',
        });
        setLoading(false);
      }
    };
    fetchCardDetails();
  }, [customer, errorHandler]);

  const toggleCardUpdate = () => setCardUpdate(!cardUpdate);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEdits((prev) => ({ ...prev, [name]: value }));
  };

  const handleCardExpiry = () => {
    let listMonth = [];
    let listYear = [];
    const date = new Date();
    const year = date.getFullYear();
    listMonth.push(
      <MenuItem selected disabled key={`month_none`} value="">
        MM
      </MenuItem>
    );
    for (let i = 1; i <= 12; i++) {
      let month = i.toString();
      if (month.length === 1) {
        month = '0' + month;
      }
      listMonth.push(
        <MenuItem value={month} key={`month_${month}`}>
          {month}
        </MenuItem>
      );
    }
    listYear.push(
      <MenuItem selected disabled key={`year_none`} value="">
        YYYY
      </MenuItem>
    );
    for (let i = 0; i <= 20; i++) {
      const expiryYear = year + i;
      listYear.push(
        <MenuItem key={`year_${expiryYear}`} value={expiryYear.toString()}>
          {expiryYear}
        </MenuItem>
      );
    }
    return (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Select
          id="expiryMonth"
          name="expiryMonth"
          size="small"
          displayEmpty
          required
          value={edits.expiryMonth ?? ''}
          onChange={(event) => handleInputChange(event)}
          sx={{ width: 80 }}
        >
          {listMonth}
        </Select>
        /
        <Select
          id="expiryYear"
          name="expiryYear"
          size="small"
          displayEmpty
          required
          value={edits.expiryYear ?? ''}
          onChange={(event) => handleInputChange(event)}
          sx={{ width: 100 }}
        >
          {listYear}
        </Select>
      </Box>
    );
  };

  const saveCard = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    let newErrors = [];
    let type;
    if (
      !edits.name ||
      edits.name.trim().length === 0 ||
      edits.name.length > 64
    ) {
      newErrors.push('Invalid Card Name');
    }
    if (edits.number) {
      const sanitizedNumber = edits.number.replace(/[.\-\s]/g, '');
      const matchedType = Object.keys(cardPatterns).find((key) =>
        cardPatterns[key].test(sanitizedNumber)
      );
      if (matchedType) {
        type = matchedType;
      } else {
        newErrors.push(
          'Unrecognized Card Type. Must be VISA, Mastercard, Discover, or American Express.'
        );
      }
    } else {
      newErrors.push('Invalid Card Number');
    }
    if (
      !edits.cvv ||
      edits.cvv.length !== (type === 'American Express' ? 4 : 3)
    ) {
      newErrors.push('Invalid Card CVV');
    }
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    if (edits.expiryMonth && edits.expiryYear) {
      if (
        edits.expiryYear > currentYear ||
        (edits.expiryYear == currentYear && edits.expiryMonth >= currentMonth)
      ) {
        edits.expiry = `${edits.expiryMonth}${edits.expiryYear.slice(-2)}`;
      } else {
        newErrors.push('Expiry date must be in the future');
      }
    } else {
      newErrors.push('Invalid Card Expiry');
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      setHasError(true);
      return false;
    }

    setIsSaving(true);

    const { cc_name, cc_expiry, cc_last_four } = await saveCardDetails(
      edits.name,
      edits.number,
      edits.expiry,
      edits.cvv,
      type,
      errorHandler
    );

    setCardDetails({
      name: cc_name,
      last_four: cc_last_four,
      expiry: cc_expiry,
    });

    setCardUpdate(false);
    setIsSaving(false);
    setEdits({});
    setErrors([]);
    setHasError(false);
  };

  if (accessLevel <= '0' || accessType === '0') {
    return null;
  }

  return (
    <Layout
      routes={BillingRoutes}
      title={PaymentMethodFormattedMessages.paymentMethod}
      accessLevel={accessLevel}
      accessType={accessType}
      customerType={customer?.customer_type}
    >
      {loading || isSaving ? (
        <Loader />
      ) : (
        <>
          <Typography variant="h2" gutterBottom>
            {PaymentMethodFormattedMessages.cardDetails}
          </Typography>

          <Grid container sx={{ maxWidth: 500, marginBottom: 4 }} spacing={2}>
            <Grid size={3}>
              <Typography fontWeight={700}>
                {PaymentMethodFormattedMessages.cardName}:
              </Typography>
            </Grid>

            <Grid size={9}>
              <Typography>{cardDetails.name}</Typography>
            </Grid>

            <Grid size={3}>
              <Typography fontWeight={700}>
                {PaymentMethodFormattedMessages.cardEnding}
              </Typography>
            </Grid>

            <Grid size={9}>
              <Typography>#### #### #### {cardDetails.last_four}</Typography>
            </Grid>

            <Grid size={3}>
              <Typography fontWeight={700}>
                {PaymentMethodFormattedMessages.cardExpiry}
              </Typography>
            </Grid>

            <Grid size={9}>
              <Typography> {cardDetails.expiry}</Typography>
            </Grid>

            <Grid size={12}>
              <Button
                variant="contained"
                color="secondary"
                onClick={toggleCardUpdate}
              >
                {cardDetails.last_four === 'N/A'
                  ? PaymentMethodFormattedMessages.addCard
                  : PaymentMethodFormattedMessages.replaceCard}
              </Button>
            </Grid>
          </Grid>

          {cardUpdate && (
            <Box component="form" onSubmit={saveCard}>
              <Stack sx={{ gap: 2, maxWidth: 500 }}>
                <Stack
                  sx={{
                    flexDirection: { md: 'row' },
                    alignItems: { md: 'center' },
                    gap: { md: 2 },
                  }}
                >
                  <InputLabel sx={{ minWidth: 150, margin: 0 }} required>
                    Card Name
                  </InputLabel>

                  <TextField
                    name="name"
                    size="small"
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Stack>

                <Stack
                  sx={{
                    flexDirection: { md: 'row' },
                    alignItems: { md: 'center' },
                    gap: { md: 2 },
                  }}
                >
                  <InputLabel sx={{ minWidth: 150, margin: 0 }} required>
                    Card Number
                  </InputLabel>

                  <TextField
                    size="small"
                    name="number"
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Stack>

                <Stack
                  sx={{
                    flexDirection: { md: 'row' },
                    alignItems: { md: 'center' },
                    gap: { md: 2 },
                  }}
                >
                  <InputLabel sx={{ minWidth: 150, margin: 0 }} required>
                    Card Expiry
                  </InputLabel>

                  {handleCardExpiry()}
                </Stack>

                <Stack
                  sx={{
                    flexDirection: { md: 'row' },
                    alignItems: { md: 'center' },
                    gap: { md: 2 },
                  }}
                >
                  <InputLabel sx={{ minWidth: 150, margin: 0 }} required>
                    CVV
                  </InputLabel>

                  <TextField
                    name="cvv"
                    size="small"
                    onChange={handleInputChange}
                    fullWidth
                    required
                  />
                </Stack>

                <img
                  src={stripeLogo}
                  alt="Powered By Stripe"
                  width="150px"
                  border="0"
                />

                <Typography>
                  For security purposes your card details will be converted into
                  a secure token before being saved in a secure vault.
                </Typography>

                {hasError && errors && <ErrorList errors={errors} />}

                <Box>
                  <Button type="submit" variant="contained" color="secondary">
                    {PaymentMethodFormattedMessages.saveCard}
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </>
      )}
    </Layout>
  );
};

export default PaymentMethod;
